import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import BottomBar from './BottomBar';
import Sidebar from './sidebar';
import Screen from './Screen';
import VonageScreen from './Screen/VonageScreen';
import {
  fetchAppointment,
  updateAppointmentSession,
  updateAppointment,
} from '../../../actions/sessions';
import { fetchUser } from '../../../actions/user';
import { updateUserAppointment } from '../../../ducks/sessions/userAppointment';
import LoadingPanel from '../../global/LoadingPanel';
import CompleteSessionDialog from './CompleteSessionDialog';
import CouplesLocationDialog from './CouplesLocationDialog';
import ClientVonagePreCall from './ClientVonagePreCall';

export function Video({
  fetchAppointment: fetchAppointmentAction,
  fetchUser: fetchUserAction,
  updateAppointmentSession: updateAppointmentSessionAction,
  updateAppointment: updateAppointmentAction,
  appointment = {},
  match,
  user,
  connected,
  updateUserAppointment: updateUserAppointmentAction,
  history = null,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(true);
  const [audioOpen, setAudioOpen] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [preCallSettings, setPreCallSettings] = useState(null);
  const [vonageDeviceSettings, setVonageDeviceSettings] = useState(null);
  const [liveCaptionsSettings, setLiveCaptionsSettings] = useState(null);
  const [couplesLocation, setCouplesLocation] = useState({
    open: false,
    answered: false,
    same: false,
  });
  const twilioControl = useRef({
    toggleCamera: () => {},
    toggleMic: () => {},
  });

  useEffect(() => {
    const appointmentId = match.params.id;
    fetchAppointmentAction(
      appointmentId,
      apptRes => {
        fetchUserAction(userRes => {
          const fetchedAppointment = apptRes.data;
          const fetchedUser = userRes.data;

          if (
            !(
              fetchedAppointment.provider_type === 'couples' &&
              fetchedUser.id ===
                fetchedAppointment.couples_appointment_detail.guest.id
            )
          ) {
            if (fetchedAppointment.status === 'scheduled') {
              updateAppointmentAction(appointmentId, {
                status: 'pending',
              });
            }

            updateUserAppointmentAction(appointmentId, {
              waiting_yn: true,
            });

            // appointmentId belonging to the current user
            const appointmentSession = _.find(
              fetchedAppointment.appointment_sessions,
              {
                user_id: _.get(fetchedUser, 'id'),
              },
            );

            // lets us know the user joined
            if (appointmentSession) {
              updateAppointmentSessionAction(appointmentSession.id, {
                joined_yn: true,
                source: 'web',
              });
            }
          }

          if (fetchedAppointment.provider_type === 'couples') {
            if (
              fetchedUser.id ===
              fetchedAppointment.couples_appointment_detail.host.id
            ) {
              setCouplesLocation({
                open: true,
                answered: false,
                same: false,
              });
            } else {
              setCouplesLocation({
                open: false,
                answered: true,
                same: false,
              });
            }
          }
        });
      },
      error => {
        Sentry.captureException(error);
        setConnectionError(true);
      },
    );
    // Match class componentDidMount: run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = () => setDialogOpen(true);

  const closeDialog = () => setDialogOpen(false);

  const toggleCamera = () => {
    const changed = twilioControl.current.toggleCamera();
    if (changed === false) return;
    setCameraOpen(prev => !prev);
  };

  const toggleMic = () => {
    twilioControl.current.toggleMic();
    setAudioOpen(prev => !prev);
  };

  const callbackForTwillio = controls => {
    if (controls.toggleCamera) {
      twilioControl.current.toggleCamera = controls.toggleCamera;
    }

    if (controls.toggleMic) {
      twilioControl.current.toggleMic = controls.toggleMic;
    }
  };

  const closeCouplesLocationDialog = isSameLocation =>
    setCouplesLocation({
      open: false,
      answered: true,
      same: isSameLocation,
    });

  const completePreCall = settings => {
    setPreCallSettings(settings);
    setCameraOpen(Boolean(settings.cameraDeviceId && settings.isCameraEnabled));
    setAudioOpen(settings.isMicrophoneEnabled);
  };

  const cancelPreCall = () => {
    if (history?.goBack) history.goBack();
    else window.history.back();
  };

  const { provider_type: sessionType } = appointment;
  const userId = user && user.id;

  if (connectionError) {
    // Shown when we could not load the session (e.g. the appointment request
    // failed). At this point the user's locale is unknown — legacy clients
    // arrive via a redirect that clears localStorage, and the profile call
    // that carries the locale is exactly what failed — so render the message
    // in both English and French rather than guessing a single language.
    return (
      <div className="global-loading-panel">
        <div className="panel-wrapper">
          {/* eslint-disable formatjs/no-literal-string-in-jsx, react/jsx-no-literals */}
          <div className="panel-message">
            <p>
              We were unable to connect you to the session. Please refresh the
              page to try again.
            </p>
            <p>
              Nous n’avons pas pu vous connecter à la séance. Veuillez
              rafraîchir la page pour réessayer.
            </p>
          </div>
          {/* eslint-enable formatjs/no-literal-string-in-jsx, react/jsx-no-literals */}
        </div>
      </div>
    );
  }

  if (!appointment.id || !user.id) {
    return <LoadingPanel />;
  }

  if (appointment.video_provider === 'vonage' && !preCallSettings) {
    return (
      <ClientVonagePreCall
        appointment={appointment}
        user={user}
        locale={user.locale || 'en'}
        onComplete={completePreCall}
        onCancel={cancelPreCall}
      />
    );
  }

  return (
    <div className="client-session">
      {(sessionType !== 'couples' || couplesLocation.answered) && (
        <div className="top-container">
          {appointment.video_provider === 'vonage' ? (
            <VonageScreen
              user={user}
              sessionType={sessionType}
              couplesSameLocation={couplesLocation.same}
              appointment={appointment}
              callbackForControls={callbackForTwillio}
              initialSettings={preCallSettings}
              onDeviceSettingsReady={setVonageDeviceSettings}
              onLiveCaptionsReady={setLiveCaptionsSettings}
            />
          ) : (
            <Screen
              userId={userId}
              channel={match.params.id}
              callbackForTwillio={callbackForTwillio}
              sessionType={sessionType}
              couplesSameLocation={couplesLocation.same}
              appointment={appointment}
            />
          )}
          <Sidebar
            user={user}
            appointment={appointment}
            locale={user.locale || 'en'}
            deviceSettings={
              appointment.video_provider === 'vonage'
                ? vonageDeviceSettings
                : null
            }
            liveCaptions={liveCaptionsSettings}
          />
        </div>
      )}
      <BottomBar
        connected={connected}
        appointment={appointment}
        toggleCamera={toggleCamera}
        toggleMic={toggleMic}
        openDialog={openDialog}
        cameraOpen={cameraOpen}
        audioOpen={audioOpen}
        user={user}
        liveCaptions={liveCaptionsSettings}
      />
      <CompleteSessionDialog open={dialogOpen} onClose={closeDialog} />
      <CouplesLocationDialog
        open={couplesLocation.open}
        onClose={closeCouplesLocationDialog}
      />
    </div>
  );
}

Video.propTypes = {
  fetchAppointment: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  updateAppointmentSession: PropTypes.func.isRequired,
  updateAppointment: PropTypes.func.isRequired,
  appointment: PropTypes.object,
  match: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  connected: PropTypes.bool.isRequired,
  updateUserAppointment: PropTypes.func.isRequired,
  history: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    appointment: state.appointments.appointment,
    providerYn: state.user.provider_yn,
    user: state.user,
  };
}

export default connect(mapStateToProps, {
  fetchAppointment,
  fetchUser,
  updateAppointmentSession,
  updateAppointment,
  updateUserAppointment,
})(Video);
